import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FaceRetouchingNaturalOutlinedIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { createPasskey, deviceName, isCancelled, passkeysSupported } from '../utils/passkeys';
import { fmtDate, fmtDateTime } from '../utils/format';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import PasswordField from '../components/auth/PasswordField';
import { SectionCard } from '../components/common/ui';
import AuthService from '../services/AuthService';
import { errorMessage, fieldErrors } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

function ChangePasswordCard() {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const set = (k) => (e) => { setForm(f => ({ ...f, [k]: e.target.value })); if (errors[k]) setErrors(x => ({ ...x, [k]: undefined })); };

    const submit = async (e) => {
        e.preventDefault();
        const v = {};
        if (!form.currentPassword) v.currentPassword = t('validation.required');
        if (form.newPassword.length < 8) v.newPassword = t('auth.register.errors.passwordShort');
        if (form.newPassword !== form.confirm) v.confirm = t('auth.register.errors.passwordMismatch');
        setErrors(v);
        if (Object.keys(v).length) return;
        setSaving(true);
        try {
            await AuthService.changePassword(form.currentPassword, form.newPassword);
            showToast(t('security.password.changed'), 'success');
            setForm({ currentPassword: '', newPassword: '', confirm: '' });
        } catch (err) {
            const code = err?.response?.data?.code;
            if (code === 'WRONG_PASSWORD') setErrors({ currentPassword: t('security.password.wrongCurrent') });
            else if (code === 'PASSWORD_UNCHANGED') setErrors({ newPassword: t('security.password.unchanged') });
            else {
                const fe = fieldErrors(err);
                if (Object.keys(fe).length) setErrors(fe); else showToast(errorMessage(err, t('common.error')), 'error');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <SectionCard title={t('security.password.title')} sx={{ mb: 2.5 }}>
            <Box component="form" onSubmit={submit} sx={{ p: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>{t('security.password.hint')}</Typography>
                <Grid container spacing={2} sx={{ maxWidth: 440 }}>
                    <Grid size={12}>
                        <PasswordField name="currentPassword" autoComplete="current-password" label={t('security.password.current')}
                                       value={form.currentPassword} onChange={set('currentPassword')} error={!!errors.currentPassword} helperText={errors.currentPassword} />
                    </Grid>
                    <Grid size={12}>
                        <PasswordField name="newPassword" autoComplete="new-password" label={t('security.password.new')}
                                       value={form.newPassword} onChange={set('newPassword')} error={!!errors.newPassword} helperText={errors.newPassword || t('auth.register.passwordHint')} />
                    </Grid>
                    <Grid size={12}>
                        <PasswordField name="confirm" autoComplete="new-password" label={t('auth.fields.confirmPassword')}
                                       value={form.confirm} onChange={set('confirm')} error={!!errors.confirm} helperText={errors.confirm} />
                    </Grid>
                </Grid>
                <Button type="submit" variant="contained" disableElevation disabled={saving} sx={{ mt: 2.5, minWidth: 150 }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('security.password.submit')}
                </Button>
            </Box>
        </SectionCard>
    );
}

/** Enable wizard: scan the QR / type the secret, confirm a code, then save the recovery codes. */
function EnableDialog({ open, onClose, onEnabled }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [setup, setSetup] = useState(null);
    const [code, setCode] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [recovery, setRecovery] = useState(null);

    React.useEffect(() => {
        if (!open) { setSetup(null); setCode(''); setError(null); setRecovery(null); return; }
        AuthService.twoFactorSetup().then(setSetup).catch((err) => showToast(errorMessage(err, t('common.error')), 'error'));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const confirm = async () => {
        if (code.trim().length < 6) { setError(t('auth.twoFactor.enterCode')); return; }
        setBusy(true); setError(null);
        try {
            const res = await AuthService.twoFactorEnable(code.trim());
            setRecovery(res.recoveryCodes);
        } catch (err) {
            setError(err?.response?.data?.code === 'TWO_FACTOR_INVALID' ? t('auth.twoFactor.invalid') : errorMessage(err, t('common.error')));
        } finally {
            setBusy(false);
        }
    };

    const copyCodes = () => {
        navigator.clipboard?.writeText(recovery.join('\n')).then(() => showToast(t('security.twoFactor.recoveryCopied'), 'success')).catch(() => {});
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
            {recovery ? (
                <>
                    <DialogTitle sx={{ fontWeight: 700 }}>{t('security.twoFactor.recoveryTitle')}</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2 }}>{t('security.twoFactor.recoveryHint')}</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', fontFamily: 'monospace', fontSize: 13.5 }}>
                            {recovery.map(c => <Box key={c}>{c}</Box>)}
                        </Box>
                        <Button startIcon={<ContentCopyIcon />} onClick={copyCodes} sx={{ mt: 1.5 }}>{t('security.twoFactor.copyCodes')}</Button>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button variant="contained" disableElevation onClick={() => { onEnabled(); onClose(); }}>{t('security.twoFactor.recoverySaved')}</Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogTitle sx={{ fontWeight: 700 }}>{t('security.twoFactor.enableTitle')}</DialogTitle>
                    <DialogContent>
                        {!setup ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box> : (
                            <>
                                <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2 }}>{t('security.twoFactor.scanHint')}</Typography>
                                <Box sx={{ textAlign: 'center', mb: 2 }}>
                                    <Box component="img" src={setup.qrDataUri} alt="QR" sx={{ width: 190, height: 190, border: '1px solid #E2E8F0', borderRadius: '10px' }} />
                                </Box>
                                <Typography sx={{ fontSize: 12, color: '#94A3B8', mb: 0.5 }}>{t('security.twoFactor.manualKey')}</Typography>
                                <Box sx={{ fontFamily: 'monospace', fontSize: 13, letterSpacing: 1, wordBreak: 'break-all', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', p: 1, mb: 2 }}>{setup.secret}</Box>
                                <TextField fullWidth size="small" autoFocus label={t('auth.twoFactor.code')} value={code}
                                           onChange={(e) => { setCode(e.target.value.replace(/\s/g, '')); if (error) setError(null); }}
                                           inputProps={{ inputMode: 'numeric', maxLength: 6 }} error={!!error} helperText={error} />
                            </>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={onClose} disabled={busy} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                        <Button variant="contained" disableElevation onClick={confirm} disabled={busy || !setup} sx={{ minWidth: 110 }}>
                            {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('security.twoFactor.confirm')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
}

function DisableDialog({ open, onClose, onDisabled }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [code, setCode] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    React.useEffect(() => { if (open) { setCode(''); setError(null); } }, [open]);

    const confirm = async () => {
        if (code.trim().length < 6) { setError(t('auth.twoFactor.enterCode')); return; }
        setBusy(true); setError(null);
        try {
            await AuthService.twoFactorDisable(code.trim());
            showToast(t('security.twoFactor.disabled'), 'success');
            onDisabled(); onClose();
        } catch (err) {
            setError(err?.response?.data?.code === 'TWO_FACTOR_INVALID' ? t('auth.twoFactor.invalid') : errorMessage(err, t('common.error')));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 700 }}>{t('security.twoFactor.disableTitle')}</DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2 }}>{t('security.twoFactor.disableHint')}</Typography>
                <TextField fullWidth size="small" autoFocus label={t('auth.twoFactor.code')} value={code}
                           onChange={(e) => { setCode(e.target.value.replace(/\s/g, '')); if (error) setError(null); }}
                           inputProps={{ inputMode: 'numeric', maxLength: 10 }} error={!!error} helperText={error} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={busy} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" color="error" disableElevation onClick={confirm} disabled={busy} sx={{ minWidth: 110 }}>
                    {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('security.twoFactor.disable')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function TwoFactorCard() {
    const { t } = useTranslation();
    const { user, updateUser } = useAuth();
    const [enableOpen, setEnableOpen] = useState(false);
    const [disableOpen, setDisableOpen] = useState(false);
    const enabled = !!user?.twoFactorEnabled;

    return (
        <SectionCard title={t('security.twoFactor.title')}>
            <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                    <ShieldOutlinedIcon sx={{ color: enabled ? '#059669' : '#94A3B8' }} />
                    <Chip size="small" icon={enabled ? <CheckCircleIcon /> : undefined}
                          label={enabled ? t('security.twoFactor.on') : t('security.twoFactor.off')}
                          sx={{ bgcolor: enabled ? 'rgba(16,185,129,0.12)' : '#F1F5F9', color: enabled ? '#047857' : '#64748B', fontWeight: 700, '& .MuiChip-icon': { color: '#059669' } }} />
                </Box>
                <Typography sx={{ fontSize: 13.5, color: '#64748B', mb: 2.5, maxWidth: 560 }}>{t('security.twoFactor.hint')}</Typography>
                {enabled
                    ? <Button variant="outlined" color="error" onClick={() => setDisableOpen(true)}>{t('security.twoFactor.disable')}</Button>
                    : <Button variant="contained" disableElevation onClick={() => setEnableOpen(true)}>{t('security.twoFactor.enable')}</Button>}
            </Box>
            <EnableDialog open={enableOpen} onClose={() => setEnableOpen(false)} onEnabled={() => updateUser({ twoFactorEnabled: true })} />
            <DisableDialog open={disableOpen} onClose={() => setDisableOpen(false)} onDisabled={() => updateUser({ twoFactorEnabled: false })} />
        </SectionCard>
    );
}

/**
 * Passkeys: sign in with Face ID / Touch ID / fingerprint. Enabled per device (the device keeps the key; with iCloud
 * Keychain it also reaches the person's other Apple devices); listed here so a lost phone can be removed.
 */
function PasskeysCard() {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [keys, setKeys] = useState(null);
    const [busy, setBusy] = useState(false);
    const [toRemove, setToRemove] = useState(null);
    const supported = passkeysSupported();
    const load = useCallback(() => AuthService.passkeys().then(setKeys).catch(() => setKeys([])), []);
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        setBusy(true);
        try {
            const { requestId, publicKey } = await AuthService.passkeyRegisterStart();
            const credential = await createPasskey(publicKey);
            await AuthService.passkeyRegisterFinish(requestId, credential, deviceName());
            showToast(t('security.passkeys.added'), 'success');
            load();
        } catch (err) {
            if (err?.name === 'InvalidStateError') showToast(t('security.passkeys.already'), 'info');
            else if (!isCancelled(err)) showToast(errorMessage(err, t('security.passkeys.failed')), 'error');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        await AuthService.deletePasskey(toRemove.id);
        setToRemove(null);
        showToast(t('security.passkeys.removed'), 'success');
        load();
    };

    return (
        <SectionCard title={t('security.passkeys.title')} sx={{ mt: 2.5 }}>
            <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <FaceRetouchingNaturalOutlinedIcon sx={{ color: keys?.length ? '#059669' : '#94A3B8' }} />
                    <Typography sx={{ fontSize: 13.5, color: '#64748B', maxWidth: 560 }}>{t('security.passkeys.hint')}</Typography>
                </Box>
                {keys?.map(k => (
                    <Box key={k.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: '1px solid #F1F5F9' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1a2332' }}>{k.name}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                                {t('security.passkeys.added_on', { date: fmtDate(k.createdAt) })}
                                {k.lastUsedAt ? ` · ${t('security.passkeys.lastUsed', { date: fmtDateTime(k.lastUsedAt) })}` : ''}
                            </Typography>
                        </Box>
                        <Button size="small" color="error" onClick={() => setToRemove(k)}>{t('security.passkeys.remove')}</Button>
                    </Box>
                ))}
                {supported ? (
                    <Button variant="contained" disableElevation onClick={add} disabled={busy} sx={{ mt: keys?.length ? 1.5 : 0 }}
                            startIcon={busy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <FaceRetouchingNaturalOutlinedIcon />}>
                        {t('security.passkeys.add')}
                    </Button>
                ) : (
                    <Typography sx={{ fontSize: 12.5, color: '#B45309' }}>{t('security.passkeys.unsupported')}</Typography>
                )}
            </Box>
            <ConfirmDialog open={!!toRemove} title={t('security.passkeys.removeTitle')} confirmLabel={t('security.passkeys.remove')}
                           text={t('security.passkeys.removeText', { name: toRemove?.name })} onConfirm={remove} onClose={() => setToRemove(null)} />
        </SectionCard>
    );
}

export default function SecurityPage() {
    const { t } = useTranslation();
    return (
        <Layout>
            <PageHeader title={t('security.title')} subtitle={t('security.subtitle')} />
            {/* Password on the left; the two ways to protect / speed up sign-in on the right (stacked on phones). */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2.5, alignItems: 'start', maxWidth: 1200,
                       '& > * > .MuiPaper-root, & > .MuiPaper-root': { m: 0 } }}>
                <ChangePasswordCard />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, minWidth: 0 }}>
                    <TwoFactorCard />
                    <PasskeysCard />
                </Box>
            </Box>
        </Layout>
    );
}
